import styled from 'styled-components';
import { LabelLarge, PrimaryButton } from '../elements';
import { connect } from 'react-redux';
import { clearAllData } from '../actions/application';
import {
  quoteHelpCopy,
  DEFAULT_QUOTE_HELP_COPY,
  INVALID_AGENCY_GUID_COPY,
  MDA_AGENT_LOOKUP_FAILURE_COPY,
} from '../helpers/quoteHelpCopy';
import { isLocal } from '../helpers/environment';
import { selectDeclinationMessages } from '../selectors';
import { selectIsAgentMDA } from '../selectors/agent';
import AlertIcon from '../icons/alert.svg?react';

interface QuoteHelpProps {
  error?:
    | string
    | {
        response?: { status?: number; config?: { url?: string } };
        config?: { url?: string };
        status?: number;
        url?: string;
      };
  clearAllData: () => void;
  declinationMessages: string[];
  isMDA: boolean;
}

const QuoteHelp = ({ error, clearAllData, declinationMessages, isMDA }: QuoteHelpProps) => {
  if (isLocal()) {
    console.log('error:', error);
  }

  const helpCopy = quoteHelpCopy(error);
  const showSubtext = helpCopy === DEFAULT_QUOTE_HELP_COPY;
  const errorObj = typeof error === 'object' ? error : undefined;
  const errorStatus = errorObj?.response?.status ?? errorObj?.status;
  const errorUrl = errorObj?.response?.config?.url ?? errorObj?.config?.url ?? errorObj?.url;

  const is401 = errorStatus === 401;
  const isProducerLookup404 = errorStatus === 404 && errorUrl?.startsWith('/validation/producer');

  if (is401) {
    return (
      <HelpCont className="CONT">
        <WarningIcon />
        <Title>{INVALID_AGENCY_GUID_COPY}</Title>
        <MoreInfo center>
          <a href="https://coterieinsurance.com/agent/">Find a Coterie Insurance Agent Near You</a>
        </MoreInfo>
      </HelpCont>
    );
  }

  if (isMDA && isProducerLookup404) {
    return (
      <HelpCont className="CONT">
        <WarningIcon />
        <Title>{MDA_AGENT_LOOKUP_FAILURE_COPY}</Title>
        <MoreInfo center>
          <a href="https://coterieinsurance.com/agent/">Find a Coterie Insurance Agent Near You</a>
        </MoreInfo>
      </HelpCont>
    );
  }

  return (
    <HelpCont className="CONT">
      <WarningIcon />
      <Title>{helpCopy}</Title>
      {showSubtext && (
        <Subtext>For help continuing this process, contact your agent or chat with our support team.</Subtext>
      )}
      {declinationMessages.length > 0 &&
        declinationMessages.map((d) => <DeclinationMessage key={d}>{d}</DeclinationMessage>)}
      <MoreInfo center>
        For more information about our appetite and coverages, <br />
        please visit <a href="https://coterieinsurance.com/producer-resources/">Agent Resources</a>.
      </MoreInfo>

      <StyledButton onClick={() => clearAllData()}>Reset application</StyledButton>
      <CenteredLabel>You may need to refresh the page after hitting the reset button.</CenteredLabel>
    </HelpCont>
  );
};

const HelpCont = styled.div`
  width: 100%;
  max-width: 800px;
  margin: 0 auto;
  text-align: center;
  padding: 24px;
  box-sizing: border-box;
`;

const Title = styled.h1`
  color: ${({ theme }) => theme.pageComponents.quoteHelp.title.textColor};
  font-family: ${({ theme }) => theme.font.typeface.primary};
  font-size: 24px;
  font-weight: 600;
  letter-spacing: 0;
  line-height: 30px;
  text-align: center;
`;

const Subtext = styled.p`
  color: ${({ theme }) => theme.pageComponents.quoteHelp.title.textColor};
  font-family: ${({ theme }) => theme.font.typeface.secondary};
  font-size: 18px;
  text-align: center;
  margin: 20px 0;
`;

const CenteredLabel = styled.p`
  color: ${({ theme }) => theme.pageComponents.quoteHelp.centeredLabel.textColor};
  font-family: ${({ theme }) => theme.font.typeface.secondary};
  font-size: ${({ theme }) => theme.pageComponents.quoteHelp.centeredLabel.fontSize};
  letter-spacing: 0.27px;
  line-height: 20px;
  text-align: center;
  margin: 30px 0;
`;

const MoreInfo = styled(LabelLarge)`
  color: ${({ theme }) => theme.pageComponents.quoteHelp.phoneNumber.textColor};
  font-size: 18px;
  margin: 40px 0 40px 0;
`;

const StyledButton = styled(PrimaryButton)`
  margin: 10px auto;
`;

const DeclinationMessage = styled.p`
  margin-left: auto;
  margin-right: auto;
  background-color: ${({ theme }) => theme.pageComponents.errorModal.background};
  color: ${({ theme }) => theme.pageComponents.errorModal.textColor};
  font-size: 15px;
  font-weight: 600;
  padding: 6px 20px;
  border-radius: 4px;
  width: fit-content;
`;

const WarningIcon = styled(AlertIcon)`
  margin-top: 100px;
  @media (max-width: 767px) {
    margin-top: 40px;
  }
  width: 48px;
  height: 48px;
  margin-bottom: 10px;

  g {
    fill: ${(props) => props.theme.pageComponents.errorModal.textColor};
  }
`;

const mapStateToProps = (state: ReduxState) => ({
  error: state.fatalError,
  declinationMessages: selectDeclinationMessages(state),
  isMDA: selectIsAgentMDA(state) ?? false,
});

const mapDispatchToProps = { clearAllData };

export default connect(mapStateToProps, mapDispatchToProps)(QuoteHelp);
